import { createContext, useContext, useEffect, useState } from 'react'
import { supabase, type User } from './supabase'
import { getLanguagePath } from './utils'
import mixpanel from './mixpanel'
import { useGoogleIdentity } from '@/hooks/use-google-identity'
import { handlePostAuthTasks } from './auth-utils'

// Helper function to ensure user ID is uppercase
const transformUser = (user: User | null): User | null => {
  if (!user) return null;
  return {
    ...user,
    id: user.id.toUpperCase()
  }
}

type AuthContextType = {
  user: User | null
  loading: boolean
  signInWithGoogleOneTap: () => Promise<User | null>
  signInWithGoogleOAuth: () => Promise<void>
  signInWithApple: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const { signInWithOneTap } = useGoogleIdentity()

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      const initialUser = transformUser(session?.user ?? null);
      setUser(initialUser);
      setLoading(false);

      // Set initial Mixpanel identification if user exists
      if (initialUser) {
        mixpanel.identify(initialUser.id); // Already uppercase from transformUser
        mixpanel.people.set({
          $email: initialUser.email,
          provider: initialUser.app_metadata?.provider,
        });
      }
    })

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUser = transformUser(session?.user ?? null);
      setUser(newUser);

      // Update Mixpanel identification
      if (newUser) {
        mixpanel.identify(newUser.id); // Already uppercase from transformUser
        mixpanel.people.set({
          $email: newUser.email,
          provider: newUser.app_metadata?.provider,
        });
      } else {
        mixpanel.reset(); // Clear user identification when signed out
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // NOT WORKING
  const signInWithGoogleOneTap = async (): Promise<User | null> => {
    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (googleClientId) {
      console.log('Attempting One Tap authentication...');
      const idToken = await signInWithOneTap(googleClientId);

      // Use Supabase's signInWithIdToken to authenticate with the Google ID token
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: idToken,
      });

      if (error) {
        throw new Error('Error with One Tap token');
      }

      console.log('One Tap sign-in successful:', data);

      // Handle post-auth tasks (profile creation, tracking, etc.)
      if (data.user) {
        // Run post-auth tasks in the background
        handlePostAuthTasks(data.user, data.session, 'one_tap').catch(error => {
          console.warn('Post-auth tasks failed:', error);
        });
      }

      // Return the user immediately - no need to wait for auth context
      return data.user ? transformUser(data.user) : null;
    } else {
      throw new Error('Google Client ID not configured');
    }
  }

  const signInWithGoogleOAuth = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        skipBrowserRedirect: false,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        },
        redirectTo: window.location.origin + getLanguagePath('auth/callback')
      }
    });

    if (error) {
      console.error('Error signing in with Google redirect:', error.message, error);
      throw error;
    }

    console.log('Google redirect OAuth initialization:', data);
  }

  const signInWithApple = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          skipBrowserRedirect: false,
          redirectTo: window.location.origin + getLanguagePath('auth/callback')
        }
      })

      if (error) {
        console.error('Error signing in with Apple:', error.message, error)
        throw error
      }

      console.log('OAuth initialization:', data)
    } catch (error) {
      console.error('Error in Apple sign-in:', error)
      throw error
    }
  }

  const signOut = async () => {
    mixpanel.reset(); // Clear user identification before signing out
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ user, loading, signInWithGoogleOneTap, signInWithGoogleOAuth, signInWithApple, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}